import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {
  ReportDownloadArgs,
  ReportGeneratedArgs,
  TimeOffReportArgs
} from '../../../../models/reports';
import moment from 'moment';
import { ReportsService } from '../../../../services/reports.service';
import { CompaniesService } from '../../../../services/companies.service';
import { Observable, BehaviorSubject, forkJoin } from 'rxjs';
import { Option } from '../../../../components/multiselect/multiselect.component';
import {
  dateRangeComponentValidator,
  dateRangeRequired
} from '../../../../utils/dateRangeValidator';
import { Person } from '../../../../models/person';
import { PagedPayload } from '../../../../models/paged-payload';
import { PeopleService } from '../../../../services/people.service';
import { EnumService } from '../../../../services/enum.service';

@Component({
  selector: 'lib-time-off-report',
  templateUrl: './time-off-report.component.html',
  styleUrls: ['./time-off-report.component.css'],
  standalone: false
})
export class TimeOffReportComponent implements OnInit {
  @Input()
  public clientCode: string;
  @Output()
  public reportGenerated = new EventEmitter<ReportGeneratedArgs>();
  @Output()
  public reportDownloadClicked = new EventEmitter<ReportDownloadArgs>();
  public reportGenerationForm: UntypedFormGroup;
  public timeOffCodes: Option[];
  public statuses: Option[];
  public people: Person[] = [];
  public divisions: Option[] = [];
  public groupBy: string[] = ['Time Off Plan'];
  public sort: string[] = ['Employee Number', 'Employee Name'];
  private readonly ENDPOINT = 'PayrollTimeOff';

  public lastPayDate: string | Date;

  public peopleObs: BehaviorSubject<Option[]>;
  constructor(
    private fb: UntypedFormBuilder,
    private companiesService: CompaniesService,
    private reportsService: ReportsService,
    private peopleService: PeopleService,
    private enumService: EnumService
  ) {}

  ngOnInit(): void {
    const timeOffCodesObs = this.companiesService.getPTOConfigs(this.clientCode);

    this.peopleObs = new BehaviorSubject<Option[]>([]);
    const personObs = this.peopleService.searchPeople(this.clientCode, {
      pageNumber: 1,
      perPage: 100
    });
    const statusObs = this.enumService.get('EmployeeStatuses');
    const deptObs = this.companiesService.getDivisions(this.clientCode);
    const totalsObs = this.companiesService.getLastPayrollTotals(this.clientCode, 0);

    forkJoin([timeOffCodesObs, personObs, statusObs, deptObs, totalsObs]).subscribe(results => {
      if (results[4] != null && results[4].length > 0) {
        this.lastPayDate = new Date(results[4][0].PayDate);
      } else {
        this.lastPayDate = new Date();
      }
      const departments: Option[] = [];
      const map = new Map();
      results[3].forEach(division => {
        division.Departments.forEach(dept => {
          if (!map.has(dept.Id)) {
            map.set(dept.Id, dept);
            departments.push({
              value: dept.Id,
              text: `${dept.DisplayId} - ${dept.Value}`
            });
          }
        });
      });
      this.divisions = departments.sort((a, b) => {
        if (a.value > b.value) return 1;
        if (a.value < b.value) return -1;
        return 0;
      });

      if (this.divisions.length > 0) {
        this.groupBy.push(...['Time Off Plan, Department', 'Time Off Plan, Division, Department']);
      }

      this.timeOffCodes = results[0]
        .map(x => {
          let text = x.Name;
          if (!x.IsActive) {
            text = `${text} - <label class='text-danger'>Inactive</label>`;
          }
          return {
            value: x.Name,
            text: text
          };
        })
        .sort((a, b) => {
          if (a.text < b.text) return -1;
          if (a.text > b.text) return 1;
          return 0;
        });
      this.statuses = results[2].map(status => {
        return {
          value: status.Id,
          text: status.Value
        };
      });
      this.people.push(...results[1].Items);
      if (results[1].Pagination.Pages > 1) {
        const obs: Observable<PagedPayload<Person>>[] = [];
        for (let i = 2; i <= results[1].Pagination.Pages; i++) {
          obs.push(
            this.peopleService.searchPeople(this.clientCode, { pageNumber: i, perPage: 100 })
          );
        }
        forkJoin(obs).subscribe((additionalResults: PagedPayload<Person>[]) => {
          additionalResults.forEach(x => this.people.push(...x.Items));
          this.peopleObs.next(this.people.map(this._convertPersonToOption));
          this._initForm();
        });
      } else {
        this.peopleObs.next(this.people.map(this._convertPersonToOption));
        this._initForm();
      }
    });
  }

  public generateReport() {
    const args = {
      args: this._getModel(),
      endpoint: this.ENDPOINT
    };
    this.reportGenerated.emit(args);
  }

  public downloadReport(format: string) {
    const now = moment().format('MMDDYYYY');
    const args = {
      args: this._getModel(),
      endpoint: this.ENDPOINT,
      filename: `Time Off Report_${this.clientCode}_${now}.${format}`,
      format
    };
    this.reportDownloadClicked.emit(args);
  }

  private _getModel() {
    const range = this.reportGenerationForm.get('dateRange').value;
    const args: TimeOffReportArgs = {
      TimeOffCodes: this.reportGenerationForm.get('timeOffCodes').value,
      FirstSortBy: this.reportGenerationForm.get('firstSortBy').value,
      GroupBy: this.reportGenerationForm.get('groupBy').value,
      Divisions: [], //Not in the spec?
      Departments: this.reportGenerationForm.get('department').value,
      EmployeeIds: this.reportGenerationForm.get('employee').value,
      SecondSortBy: '',
      StartDate: range.start,
      EndDate: range.end
    };
    return args;
  }

  private _initForm() {
    this.reportGenerationForm = this.fb.group(
      {
        dateRange: this.fb.control({ start: this.lastPayDate, end: this.lastPayDate }, [
          dateRangeRequired,
          dateRangeComponentValidator
        ]),
        timeOffCodes: this.fb.control(
          this.timeOffCodes.map(x => x.value),
          ArrayMinLength(1)
        ),
        firstSortBy: this.fb.control(null, Validators.required),
        employeeStatus: this.fb.control(
          this.statuses.map(x => x.value),
          ArrayMinLength(1)
        ),
        department: this.fb.control(this.divisions.map(x => x.value)),
        employee: this.fb.control(
          this.people.map(x => x.Id),
          ArrayMinLength(1)
        ),
        groupBy: this.fb.control(null, Validators.required)
      },
      { validators: [MinimumOneCodeSelected] }
    );
    if (this.divisions.length > 0) {
      this.reportGenerationForm.get('department').setValidators(ArrayMinLength(1));

      this.reportGenerationForm.get('department').valueChanges.subscribe((value: string[]) => {
        //grab the Status field too
        const status: string[] = this.reportGenerationForm.get('employeeStatus').value;

        const peopleInDepartment = this.people.filter(x =>
          value.some(y => y == x.Department.toString())
        );
        const peopleAlsoInStatus = peopleInDepartment.filter(x =>
          status.some(y => y == x.CurrentStatus.StatusId.toString())
        );
        this.peopleObs.next(peopleAlsoInStatus.map(this._convertPersonToOption));
      });
    }
    this.reportGenerationForm.get('employeeStatus').valueChanges.subscribe((value: string[]) => {
      //grab the Department field too
      const status: string[] = this.reportGenerationForm.get('department').value;

      let filteredPeople = this.people.filter(x =>
        value.some(y => y == x.CurrentStatus.StatusId.toString())
      );
      if (this.divisions.length > 0) {
        filteredPeople = filteredPeople.filter(x => status.some(y => y == x.Department.toString()));
      }
      this.peopleObs.next(filteredPeople.map(this._convertPersonToOption));
    });
  }

  private _convertPersonToOption(person: Person): Option {
    let middleInitial = '';
    if (person.MiddleName != null && person.MiddleName != '') {
      middleInitial = ` ${person.MiddleName[0]}`;
    }
    return {
      value: person.Id,
      text: `${person.LastName}, ${person.FirstName}${middleInitial}`
    };
  }
}

export const ArrayMinLength: (minLength: number) => ValidatorFn = minLength => {
  return (field: AbstractControl) => {
    const val = field.value;
    if (val == null || val.length == 0) {
      return { minArrayLength: true };
    }
    return null;
  };
};

export const MinimumOneCodeSelected: ValidatorFn = (form: AbstractControl) => {
  const field = form.get('timeOffCodes').value;
  if (field == null || field.length == 0) {
    return { mustSelectCode: true };
  }
  return null;
};
